import { memo } from 'react';
import {
  Flex,
  Grid,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { HamburgerIcon } from '@chakra-ui/icons';
import { AdminTag, LatecomerTag, RankChangeIndicator } from '@/components';
import { isPendingLeagueRank, memberHasList } from '@/helpers/leagues/leagueMemberSync';
import type { leagueMember } from '@/models/user';
import { pageButtonProps } from '@/theme/sitePageTypography';

/** Rank | arrow | name | tags | points — rank column fits 4-digit ranks (1000+). */
export const LEADERBOARD_ROW_COLUMNS =
  'minmax(2.75rem, max-content) 1.25rem minmax(0, 1fr) auto minmax(2.75rem, max-content)';
export const LEADERBOARD_ROW_COLUMNS_COMPACT =
  'minmax(2.5rem, max-content) 1rem minmax(0, 1fr) auto minmax(2.5rem, max-content)';
export const LEADERBOARD_ROW_PY = 2;
export const LEADERBOARD_ROW_PX = 3;
export const LEADERBOARD_ROW_MIN_H = '2.5rem';
export const LEADERBOARD_YOU_BORDER = '2px solid';
export const LEADERBOARD_YOU_BORDER_COLOR = 'green.500';

export interface LeaderboardMemberRow {
  memberUid: string;
  member: leagueMember;
  displayRank: number;
  isYou: boolean;
  isLeagueAdmin: boolean;
  showRankChange: boolean;
  listPosition: number;
  isAdminViewer: boolean;
  canKick: boolean;
  tournamentStarted: boolean;
}

export const LeaderboardRow = memo(function LeaderboardRow(
  props: LeaderboardMemberRow & {
    readOnly?: boolean;
    onTransferOwnership?: (uid: string) => void;
    onKick?: (uid: string) => void;
    onViewMemberList?: (uid: string, member: leagueMember) => void;
  },
) {
  const {
    memberUid,
    member,
    displayRank,
    isYou,
    isLeagueAdmin,
    showRankChange,
    listPosition,
    isAdminViewer,
    canKick,
    tournamentStarted,
    readOnly,
    onTransferOwnership,
    onKick,
    onViewMemberList,
  } = props;

  const hasList = memberHasList(member);
  const displayName =
    !!member.name && member.name.trim().length > 0 ? member.name.trim() : 'Anonymous';
  const listIsViewable = hasList && !!member.listVisibleInLeagues && !!onViewMemberList;
  const rankPending = hasList && isPendingLeagueRank(member.currentRank);
  const pointsText = hasList
    ? tournamentStarted
      ? String(member.currentScore)
      : '0'
    : '—';
  const showAdminMenu = !readOnly && isAdminViewer && canKick && onTransferOwnership && onKick;
  const columns = useBreakpointValue({
    base: LEADERBOARD_ROW_COLUMNS_COMPACT,
    sm: LEADERBOARD_ROW_COLUMNS,
  }) ?? LEADERBOARD_ROW_COLUMNS_COMPACT;
  const rowPx = useBreakpointValue({ base: 2, sm: LEADERBOARD_ROW_PX }) ?? 2;

  return (
    <Grid
      templateColumns={columns}
      columnGap={2}
      alignItems='center'
      py={LEADERBOARD_ROW_PY}
      px={rowPx}
      borderRadius='md'
      background='listItemBackground'
      border={isYou ? LEADERBOARD_YOU_BORDER : undefined}
      borderColor={isYou ? LEADERBOARD_YOU_BORDER_COLOR : undefined}
      minH={LEADERBOARD_ROW_MIN_H}
      w='full'
    >
      <Text
        fontSize='sm'
        fontWeight='medium'
        textAlign='left'
        whiteSpace='nowrap'
        flexShrink={0}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {!hasList || rankPending ? '—' : displayRank}
      </Text>

      <Flex justify='center' align='center' w='1.25rem' flexShrink={0}>
        {hasList && !rankPending && showRankChange ? (
          <RankChangeIndicator
            memberData={member}
            getListPosition={() => listPosition}
            fontSize='xs'
          />
        ) : null}
      </Flex>

      {listIsViewable ? (
        <Text
          as='button'
          type='button'
          fontSize='sm'
          noOfLines={1}
          textAlign='left'
          minW={0}
          textDecoration='underline'
          cursor='pointer'
          onClick={() => onViewMemberList(memberUid, member)}
        >
          {displayName}
        </Text>
      ) : (
        <Text fontSize='sm' noOfLines={1} textAlign='left' minW={0}>
          {displayName}
        </Text>
      )}

      <HStack spacing={1} justify='flex-end' flexWrap='wrap' minW={0}>
        {isLeagueAdmin ? <AdminTag /> : null}
        {member.latecomer ? <LatecomerTag /> : null}
        {showAdminMenu ? (
          <Menu isLazy>
            <MenuButton
              as={IconButton}
              {...pageButtonProps}
              aria-label='Member actions'
              icon={<HamburgerIcon boxSize={3} />}
              variant='ghost'
              minW={6}
              h={6}
            />
            <MenuList fontSize='sm'>
              <MenuItem onClick={() => onTransferOwnership(memberUid)}>Make admin</MenuItem>
              <MenuItem onClick={() => onKick(memberUid)}>Kick from league</MenuItem>
            </MenuList>
          </Menu>
        ) : null}
      </HStack>

      <Text
        fontSize='sm'
        textAlign='right'
        flexShrink={0}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
        lineHeight='short'
      >
        {pointsText}
      </Text>
    </Grid>
  );
});
